import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    ArrowLeftIcon,
    DocumentTextIcon,
    PencilSquareIcon,
    PlusIcon
} from '@heroicons/react/24/outline';
import { fetchTimeline } from '../../../../../services/timeline';
import { VcaTriggerWizard } from './VcaTriggerWizard';
import type { ActionRunnerProps, TimelineEvent } from '../../types';
import type { VcaTriggerConfig } from './vca-trigger-config';

type View = { kind: 'list' } | { kind: 'create' } | { kind: 'edit'; event: TimelineEvent };

function extractNotes(event: TimelineEvent): string {
    const payload = event.payload as Record<string, unknown> | null;
    return (payload?.notes as string) ?? '';
}

function capitalize(word: string): string {
    return word.length === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1);
}

type VcaTriggerLandingPageProps = ActionRunnerProps & {
    config: VcaTriggerConfig;
    // Forwarded to the wizard so the work-queue surface can refresh + return to
    // the patient list once a new trigger transitions the case.
    onSubmitted?: (landedOn: string) => void;
};

function VcaTriggerLandingPage({
    patientId,
    onBack,
    onDataChange,
    config,
    onSubmitted
}: VcaTriggerLandingPageProps) {
    const [view, setView] = useState<View>({ kind: 'list' });
    const [events, setEvents] = useState<TimelineEvent[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const loadEvents = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const result = await fetchTimeline(patientId, {
                type: config.eventType,
                isLatest: true,
                limit: 50
            });
            setEvents(result.data);
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : `Failed to load ${config.plural}`;
            setError(message);
        } finally {
            setLoading(false);
        }
    }, [patientId, config.eventType, config.plural]);

    useEffect(() => {
        loadEvents();
    }, [loadEvents]);

    const sortedEvents = useMemo(
        () =>
            [...events].sort(
                (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
            ),
        [events]
    );

    const editableEventId =
        sortedEvents.length > 0 && sortedEvents[0].status === 'active' ? sortedEvents[0].id : null;

    const handleWizardBack = useCallback(() => {
        setView({ kind: 'list' });
        loadEvents();
        onDataChange?.();
    }, [loadEvents, onDataChange]);

    if (view.kind === 'create') {
        return (
            <VcaTriggerWizard
                patientId={patientId}
                onBack={handleWizardBack}
                config={config}
                onSubmitted={onSubmitted}
            />
        );
    }

    if (view.kind === 'edit') {
        return (
            <VcaTriggerWizard
                patientId={patientId}
                onBack={handleWizardBack}
                editingEvent={view.event}
                config={config}
            />
        );
    }

    const SingularCapitalized = capitalize(config.singular);
    const headerLabel = `Trigger ${config.singular}`;
    const emptyTitle = `No ${config.plural} triggered yet`;
    const emptyHint = `Create a trigger to move the patient into the ${config.singular} workflow.`;
    const rowLabel = `${SingularCapitalized} triggered`;

    return (
        <div className="flex flex-col flex-1 min-h-0">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 flex-shrink-0">
                <button
                    onClick={onBack}
                    className="flex items-center gap-1.5 text-gray-500 hover:text-gray-800 transition-colors text-sm"
                >
                    <ArrowLeftIcon className="w-3.5 h-3.5" />
                    Back to actions
                </button>
                <div className="text-sm font-semibold text-gray-800">{headerLabel}</div>
                <button
                    onClick={() => setView({ kind: 'create' })}
                    className="flex items-center gap-1.5 bg-gray-900 text-white text-xs font-medium px-3 py-1.5 rounded-lg hover:bg-gray-800 transition-colors"
                >
                    <PlusIcon className="w-3.5 h-3.5" />
                    New trigger
                </button>
            </div>

            <div className="flex-1 min-h-0 overflow-auto">
                {loading && (
                    <div className="flex items-center justify-center py-12">
                        <div className="w-5 h-5 rounded-full border-2 border-gray-300 border-t-gray-800 animate-spin" />
                    </div>
                )}

                {error && (
                    <div className="flex items-center justify-center py-12">
                        <div className="text-xs text-red-500">{error}</div>
                    </div>
                )}

                {!loading && !error && sortedEvents.length === 0 && (
                    <div className="flex flex-col items-center justify-center h-full gap-4 p-8">
                        <div className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center">
                            <DocumentTextIcon className="w-6 h-6 text-gray-400" />
                        </div>
                        <div className="text-center">
                            <div className="text-sm font-semibold text-gray-800">{emptyTitle}</div>
                            <div className="text-xs text-gray-500 mt-1">{emptyHint}</div>
                        </div>
                        <button
                            onClick={() => setView({ kind: 'create' })}
                            className="flex items-center gap-1.5 bg-gray-900 text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors"
                        >
                            <PlusIcon className="w-4 h-4" />
                            New trigger
                        </button>
                    </div>
                )}

                {!loading && !error && sortedEvents.length > 0 && (
                    <div className="p-5 flex flex-col gap-2">
                        {sortedEvents.map((event) => {
                            const isEditable = event.id === editableEventId;
                            const date = new Date(event.createdAt);
                            const formattedDate = date.toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                            });
                            const formattedTime = date.toLocaleTimeString('en-US', {
                                hour: 'numeric',
                                minute: '2-digit'
                            });
                            const notes = extractNotes(event);
                            const preview = notes.length > 80 ? `${notes.slice(0, 80)}...` : notes;

                            return (
                                <div
                                    key={event.id}
                                    className="flex items-center justify-between px-4 py-3 rounded-xl border border-gray-200 bg-white hover:border-gray-300 transition-colors"
                                >
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                                            <DocumentTextIcon className="w-4 h-4 text-gray-500" />
                                        </div>
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm font-medium text-gray-800 truncate">
                                                    {rowLabel}
                                                </span>
                                                {event.version > 1 && (
                                                    <span className="text-[10px] font-medium text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded flex-shrink-0">
                                                        v{event.version}
                                                    </span>
                                                )}
                                                {event.status === 'invalidated' && (
                                                    <span className="text-[10px] font-medium text-red-600 bg-red-50 px-1.5 py-0.5 rounded flex-shrink-0">
                                                        Invalidated
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-xs text-gray-500 mt-0.5 truncate">
                                                {formattedDate} at {formattedTime}
                                                {preview && (
                                                    <span className="ml-2 text-gray-400">
                                                        - {preview}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        {isEditable && (
                                            <button
                                                onClick={() => setView({ kind: 'edit', event })}
                                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                                            >
                                                <PencilSquareIcon className="w-3.5 h-3.5" />
                                                Edit
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}

export { VcaTriggerLandingPage };
